import React from 'react';
import { motion } from 'framer-motion';
import SubtypePickerCard from './SubtypePickerCard';

interface Subtype {
  type: string;
  label: string;
  Icon: React.FC<{ className?: string }>;
}

interface SubtypePickerStripProps {
  subtypes: Subtype[];
  onPick: (type: string) => void;
  disabled?: boolean;
}

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * SubtypePickerStrip — centered responsive grid of picker cards.
 *
 * Cards stagger in on mount (50ms each) for a cohesive reveal. On mobile the
 * row becomes horizontally scrollable with snap points. On desktop it centers
 * as a flex-wrap row. All motion hardware-accelerated via `transform`.
 */
export default function SubtypePickerStrip({
  subtypes,
  onPick,
  disabled,
}: SubtypePickerStripProps) {
  return (
    <div
      role="group"
      aria-label="Choose a question type"
      className="
        flex gap-3 flex-nowrap md:flex-wrap md:justify-center
        overflow-x-auto md:overflow-visible scrollbar-none
        snap-x snap-mandatory
        -mx-1 px-1 py-1
      "
    >
      {subtypes.map(({ type, label, Icon }, idx) => (
        <motion.div
          key={type}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: EASE_OUT, delay: idx * 0.04 }}
          className="snap-start"
        >
          <SubtypePickerCard
            title={label}
            icon={<Icon className="w-8 h-8" />}
            onClick={() => onPick(type)}
            disabled={disabled}
          />
        </motion.div>
      ))}
    </div>
  );
}
